/**
 * partner logos actions
 */

import {
  PARTNER_LOGOS_CHANGE,
  PARTNER_LOGOS_EDIT_CHANGE,
  PARTNER_LOGOS_ADD,
  PARTNER_LOGOS_DELETE,
  PARTNER_LOGOS_GET,
  PARTNER_LOGOS_GET_ALL,
  PARTNER_LOGOS_RESET,
  SET_PARTNER_LOGOS_LOADING,
  SET_PARTNER_LOGOS_FORM_ERRORS,
  SET_PARTNER_LOGOS_FORM_EDIT_ERRORS,
} from './constants';
import axios from 'axios';
import { API_URL } from '../../constants';
import { showNotification } from '../Notification/actions';
import handleError from '../../utils/error';
import { allFieldsValidation } from '../../utils/validation';

const getImageFile = (image) => {
  if (!image) return null;
  if (Array.isArray(image)) return image[0] || null;
  if (image instanceof File) return image;
  return null;
};

export const partnerLogoChange = (name, value) => {
  return {
    type: PARTNER_LOGOS_CHANGE,
    payload: { [name]: value },
  };
};

export const partnerLogoEditChange = (name, value) => {
  return {
    type: PARTNER_LOGOS_EDIT_CHANGE,
    payload: { [name]: value },
  };
};

export const resetPartnerLogo = () => {
  return async (dispatch) => {
    dispatch({ type: PARTNER_LOGOS_RESET });
  };
};

const setPartnerLogosLoading = (loading) => {
  return {
    type: SET_PARTNER_LOGOS_LOADING,
    payload: loading,
  };
};

export const fetchPartnerLogo = (id) => {
  return async (dispatch) => {
    dispatch(setPartnerLogosLoading(true));
    try {
      const response = await axios.get(`${API_URL}/partner-logos/${id}`);
      if (response.status === 200 && response.data.partnerLogo) {
        dispatch({ type: PARTNER_LOGOS_GET, payload: response.data.partnerLogo });
      }
    } catch (error) {
      dispatch(showNotification('error', 'Failed to fetch partner logo'));
      handleError(error, dispatch);
    } finally {
      dispatch(setPartnerLogosLoading(false));
    }
  };
};

export const fetchPartnerLogos = () => {
  return async (dispatch) => {
    dispatch(setPartnerLogosLoading(true));
    try {
      const response = await axios.get(`${API_URL}/partner-logos`);
      if (response.status === 200 && response.data.partnerLogos) {
        dispatch({ type: PARTNER_LOGOS_GET_ALL, payload: response.data.partnerLogos });
      }
    } catch (error) {
      dispatch(showNotification('error', 'Failed to fetch partner logos'));
      handleError(error, dispatch);
    } finally {
      dispatch(setPartnerLogosLoading(false));
    }
  };
};

export const addPartnerLogo = (navigate) => {
  return async (dispatch, getState) => {
    dispatch(setPartnerLogosLoading(true));
    try {
      const rules = {
        name: 'required',
        image: 'required',
      };

      const partnerLogo = getState().partnerLogos.partnerLogoFormData;
      const imageFile = getImageFile(partnerLogo.image);

      const newPartnerLogo = {
        name: partnerLogo.name,
        image: imageFile,
      };

      const { isValid, errors } = allFieldsValidation(newPartnerLogo, rules, {
        'required.name': 'Name is required.',
        'required.image': 'Image is required.',
      });

      if (!isValid) {
        dispatch({ type: SET_PARTNER_LOGOS_FORM_ERRORS, payload: errors });
        return;
      }

      const formData = new FormData();
      formData.set('name', newPartnerLogo.name);
      formData.set('isEnabled', String(partnerLogo.isEnabled !== false));
      formData.append('image', imageFile);

      const response = await axios.post(`${API_URL}/partner-logos/add`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      if (response.data.success === true) {
        dispatch(showNotification('success', response.data.message));
        dispatch({ type: PARTNER_LOGOS_ADD, payload: response.data.partnerLogo });
        dispatch(resetPartnerLogo());
        navigate(-1);
      }
    } catch (error) {
      handleError(error, dispatch, 'Error saving partner logo, try again!');
    } finally {
      dispatch(setPartnerLogosLoading(false));
    }
  };
};

export const updatePartnerLogo = (navigate) => {
  return async (dispatch, getState) => {
    dispatch(setPartnerLogosLoading(true));
    try {
      const rules = {
        name: 'required',
      };

      const partnerLogo = getState().partnerLogos.partnerLogo;
      const imageFile = getImageFile(partnerLogo.image);

      const updatedPartnerLogo = {
        name: partnerLogo.name,
      };

      const { isValid, errors } = allFieldsValidation(updatedPartnerLogo, rules, {
        'required.name': 'Name is required.',
      });

      if (!isValid) {
        dispatch({ type: SET_PARTNER_LOGOS_FORM_EDIT_ERRORS, payload: errors });
        return;
      }

      const formData = new FormData();
      formData.set('name', updatedPartnerLogo.name);
      formData.set('isEnabled', String(partnerLogo.isEnabled !== false));
      if (imageFile) {
        formData.append('image', imageFile);
      }

      const response = await axios.put(`${API_URL}/partner-logos/${partnerLogo._id}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      if (response.data.success === true) {
        dispatch(showNotification('success', response.data.message));
        dispatch(resetPartnerLogo());
        navigate(-1);
      }
    } catch (error) {
      handleError(error, dispatch, 'Error updating partner logo, try again!');
    } finally {
      dispatch(setPartnerLogosLoading(false));
    }
  };
};

export const togglePartnerLogo = (id, isEnabled) => {
  return async (dispatch, getState) => {
    try {
      const partnerLogo = getState().partnerLogos.partnerLogosList.find(
        (logo) => logo._id === id
      );

      const formData = new FormData();
      formData.set('name', partnerLogo?.name || '');
      formData.set('isEnabled', String(isEnabled));

      const response = await axios.put(`${API_URL}/partner-logos/${id}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      if (response.data.success === true) {
        dispatch({ type: PARTNER_LOGOS_GET_ALL, payload: getState().partnerLogos.partnerLogosList.map((logo) => (
          logo._id === id ? { ...logo, isEnabled } : logo
        )) });
      }
    } catch (error) {
      handleError(error, dispatch, 'Error updating partner logo, try again!');
    }
  };
};

export const deletePartnerLogo = (id, navigate) => {
  return async (dispatch) => {
    try {
      const response = await axios.delete(`${API_URL}/partner-logos/${id}`);

      if (response.data.success === true) {
        dispatch(showNotification('success', response.data.message));
        dispatch({ type: PARTNER_LOGOS_DELETE, payload: id });
        navigate(-1);
      }
    } catch (error) {
      handleError(error, dispatch, 'Error deleting partner logo, try again!');
    }
  };
};
